import { IStage } from '@forwardflow/store/onboardingStoreV2'
import {
   IStageMapGroup,
   TStageGroupId,
   TStopStageKey,
} from '@forwardflow/types/onboarding'
import {
   addStageToGroupUtil,
   addTagToGroupUtil,
   extractStagesAndTagsByGroup,
   isNestedStopKey,
   removeStageFromAllGroupsUtil,
   removeStageFromGroupUtil,
   removeTagFromAllGroupsUtil,
   removeTagFromGroupUtil,
} from '@forwardflow/features/dnd-stage-mapping/utils'
import { IStageMapGroup as TDndGroup } from '@forwardflow/features/dnd-stage-mapping/interfaces'

/*
 * The three "leave alone" questions, each answered with stages and tags from
 * Follow Up Boss. Every answer stops Forward Flow; they differ only in why,
 * which is what the customer understands. Each maps to the stop buckets the
 * backend already knows, so Settings → Stage Mapping stays in sync. The
 * nurturing and appointment buckets are filled from the suggestions quietly.
 */

/** Every group a stage or tag can be mapped to. */
export type TBucketKey =
   | Extract<
        TStageGroupId,
        'ff-nurturing' | 'ff-appointment-set' | 'ff-appointment-missed'
     >
   | TStopStageKey

export const ALL_BUCKETS: TBucketKey[] = [
   'ff-nurturing',
   'ff-appointment-set',
   'ff-appointment-missed',
   'ff-under-contract',
   'ff-active-client',
   'ff-closed-client',
   'ff-do-not-contact',
   'ff-has-agent',
   'ff-other-stop',
]

export type TStopGroup = 'working' | 'done' | 'never'

export interface IStopQuestion {
   id: TStopGroup
   /** The rail row. */
   title: string
   question: string
   hint: string
   /** Buckets whose items and suggestions this question shows. */
   buckets: TBucketKey[]
   /** Where a manual pick goes. */
   target: TBucketKey
}

export const STOP_QUESTIONS: IStopQuestion[] = [
   {
      id: 'working',
      title: 'Leads you are working',
      question: 'Which leads are you already working yourself?',
      hint: 'The stages or tags that mean "I am on it". We never call these leads; they are yours.',
      buckets: ['ff-active-client', 'ff-under-contract'],
      target: 'ff-active-client',
   },
   {
      id: 'done',
      title: 'Leads already done',
      question: 'Which leads are already done?',
      hint: 'Closed, sold, lost, or working with another agent. We leave these alone too.',
      buckets: ['ff-closed-client', 'ff-has-agent'],
      target: 'ff-closed-client',
   },
   {
      id: 'never',
      title: 'Do not contact',
      question: 'Who must we never contact?',
      hint: 'Do not call, trash, opted out. Add anything else that should stop us.',
      buckets: ['ff-do-not-contact', 'ff-other-stop'],
      target: 'ff-other-stop',
   },
]

/*
 * The drag-and-drop feature declares its own copy of `IStageMapGroup` whose
 * tags are `IStage[]`; the store's copy types them as `ITag[]`. Same shape at
 * runtime, so the two casts bridge them (the DnD step does the same).
 */
const dnd = (groups: IStageMapGroup[]) => groups as unknown as TDndGroup[]
const back = (groups: TDndGroup[]) => groups as unknown as IStageMapGroup[]

/** An item the customer can pick: a FUB stage or a FUB tag. */
export type TMappedItem = IStage & { type: 'stage' | 'tag' }

/** Stable key for an item, so a tag and a stage with the same id never clash. */
export const itemKey = (item: Pick<TMappedItem, 'id' | 'type'>) =>
   `${item.type}:${item.id}`

const timelineFor = (groups: IStageMapGroup[], key: TBucketKey) =>
   key === 'ff-nurturing'
      ? groups.find((g) => g.group === 'ff-nurturing')?.timeline
      : undefined

/** Stages and tags currently mapped to a bucket. */
export const itemsIn = (
   groups: IStageMapGroup[],
   key: TBucketKey
): TMappedItem[] => {
   const { stages, tags } = extractStagesAndTagsByGroup(key, dnd(groups))
   return [
      ...stages
         .filter((s) => s?.id && s?.name?.trim())
         .map((s) => ({ ...s, type: 'stage' as const })),
      ...tags
         .filter((t) => t?.name?.trim())
         .map((t) => ({ ...t, id: t.id || t.name, type: 'tag' as const })),
   ]
}

/** Every item mapped anywhere, keyed by `itemKey`. */
export const assignedKeys = (groups: IStageMapGroup[]): Set<string> =>
   new Set(ALL_BUCKETS.flatMap((b) => itemsIn(groups, b).map(itemKey)))

/** Add an item to a bucket, moving it out of any other bucket first. */
export const addItem = (
   groups: IStageMapGroup[],
   item: TMappedItem,
   key: TBucketKey
): IStageMapGroup[] => {
   const timeline = timelineFor(groups, key)
   if (item.type === 'tag') {
      const cleared = removeTagFromAllGroupsUtil(dnd(groups), item.id)
      return back(addTagToGroupUtil(cleared, item, key, timeline))
   }
   const cleared = removeStageFromAllGroupsUtil(dnd(groups), item.id)
   return back(addStageToGroupUtil(cleared, item, key, timeline))
}

/** Remove an item from a bucket. */
export const removeItem = (
   groups: IStageMapGroup[],
   item: TMappedItem,
   key: TBucketKey
): IStageMapGroup[] => {
   const timeline = timelineFor(groups, key)
   return back(
      item.type === 'tag'
         ? removeTagFromGroupUtil(dnd(groups), item.id, key, timeline)
         : removeStageFromGroupUtil(dnd(groups), item.id, key, timeline)
   )
}

/**
 * The suggestions for one bucket, flattened from the suggested-mappings
 * response (same shape as the saved groups), minus anything already mapped.
 */
export const suggestionsFor = (
   suggestions: IStageMapGroup[] | null | undefined,
   groups: IStageMapGroup[],
   key: TBucketKey
): TMappedItem[] => {
   if (!suggestions?.length) return []
   const taken = assignedKeys(groups)
   const seen = new Set<string>()
   const out: TMappedItem[] = []
   const push = (item: TMappedItem) => {
      const k = itemKey(item)
      if (!item.name?.trim() || taken.has(k) || seen.has(k)) return
      seen.add(k)
      out.push(item)
   }
   const collect = (stages?: IStage[], tags?: IStage[]) => {
      stages?.forEach((s) => {
         const id = s?.id?.toString()
         if (id) push({ id, name: s.name, description: '', type: 'stage' })
      })
      tags?.forEach((t) => {
         if (t?.name)
            push({ id: t.name, name: t.name, description: '', type: 'tag' })
      })
   }
   suggestions.forEach((mapping) => {
      if (mapping.group === key)
         collect(mapping.stages, mapping.tags as IStage[] | undefined)
      if (mapping.group === 'ff-stopped' && isNestedStopKey(key)) {
         mapping.types
            ?.filter((t) => t.key === key)
            .forEach((t) => collect(t.stages, t.tags as IStage[] | undefined))
      }
   })
   return out
}

/** Apply every suggestion that is not already mapped somewhere. */
export const applyAllSuggestions = (
   groups: IStageMapGroup[],
   suggestions: IStageMapGroup[] | null | undefined
): IStageMapGroup[] =>
   ALL_BUCKETS.reduce(
      (acc, bucket) =>
         suggestionsFor(suggestions, acc, bucket).reduce(
            (inner, item) => addItem(inner, item, bucket),
            acc
         ),
      groups
   )

export const hasAnySelection = (groups: IStageMapGroup[]) =>
   assignedKeys(groups).size > 0

/** The items a question shows, each with the bucket it sits in. */
export const chosenFor = (groups: IStageMapGroup[], q: IStopQuestion) =>
   q.buckets.flatMap((b) =>
      itemsIn(groups, b).map((item) => ({ ...item, bucket: b }))
   )

/** The suggestions a question offers, each with its bucket. */
export const suggestedFor = (
   suggestions: IStageMapGroup[] | null | undefined,
   groups: IStageMapGroup[],
   q: IStopQuestion
) =>
   q.buckets.flatMap((b) =>
      suggestionsFor(suggestions, groups, b).map((item) => ({
         ...item,
         bucket: b,
      }))
   )

/** Every stage and tag mapped to any stop question. */
export const allStops = (groups: IStageMapGroup[]) =>
   STOP_QUESTIONS.flatMap((q) => chosenFor(groups, q))
